<template lang="pug">
.rangeSelectComponent
  .line(ref="line" @mousedown="onLineClick")
    .range(:style="rangeStyle")
    .handle(
      ref="handle"
      :style="handleStyle"
      :class="{ _disabled: disabled, _empty: displayedValue === null }"
      @mousedown.prevent="onStart"
      @touchstart.prevent="onStart"
    )
      .value(v-if="displayedValue !== null") {{ displayedValue }}
</template>

<script lang="ts" setup>
import { computed, onMounted, onBeforeUnmount, ref } from 'vue'

interface Props {
  min: number
  max: number
  modelValue: number | null | undefined
  step?: number
  disabled?: boolean
  hasSavedValue?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  step: 1,
  hasSavedValue: false
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: number): void
}>()

const line = ref<HTMLDivElement>()
const handle = ref<HTMLDivElement>()

const lineWidth = ref(0)
const handleWidth = ref(0)

const isMoving = ref(false)
const startX = ref(0)
const startValue = ref(0)
const currentDragValue = ref<number | null>(null)
const hasInteracted = ref(props.hasSavedValue)

const toNumber = (v: unknown, fallback = 0) => {
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : fallback
}

const minN = computed(() => toNumber(props.min, 0))
const maxN = computed(() => toNumber(props.max, 100))
const stepN = computed(() => {
  const s = toNumber(props.step, 1)
  return s > 0 ? s : 1
})

const clamp = (v: number) => Math.max(minN.value, Math.min(maxN.value, v))

const stepsCount = computed(() => Math.max(0, Math.round((maxN.value - minN.value) / stepN.value)))

const stepWidth = computed(() => {
  const available = Math.max(0, lineWidth.value - handleWidth.value)
  return stepsCount.value ? available / stepsCount.value : 0
})

const displayedValue = computed<number | null>(() => {
  if (!hasInteracted.value && !isMoving.value) return null
  if (isMoving.value) return currentDragValue.value
  return clamp(toNumber(props.modelValue, minN.value))
})

const offsetPx = computed(() => {
  if (!stepWidth.value) return 0

  const value = displayedValue.value ?? minN.value
  const steps = (value - minN.value) / stepN.value

  return steps * stepWidth.value + handleWidth.value / 2
})

const rangeStyle = computed(() => ({
  width: displayedValue.value === null ? '0px' : `${Math.max(0, offsetPx.value)}px`
}))

const handleStyle = computed(() => ({
  left: `${Math.max(0, offsetPx.value)}px`
}))

const updateSizes = () => {
  lineWidth.value = line.value?.offsetWidth ?? 0
  handleWidth.value = handle.value?.getBoundingClientRect().width ?? 0
}

const getPageX = (e: MouseEvent | TouchEvent) =>
  e instanceof MouseEvent ? e.pageX : e.touches[0].pageX

const onStart = (e: MouseEvent | TouchEvent) => {
  if (props.disabled) return

  hasInteracted.value = true

  updateSizes()

  isMoving.value = true
  startX.value = getPageX(e)

  startValue.value = clamp(toNumber(props.modelValue, minN.value))
}

const onMove = (e: MouseEvent | TouchEvent) => {
  if (!isMoving.value) return

  const dx = getPageX(e) - startX.value

  if (stepWidth.value <= 0) return

  currentDragValue.value = clamp(startValue.value + Math.round(dx / stepWidth.value) * stepN.value)
}

const onEnd = () => {
  if (!isMoving.value) return

  currentDragValue.value !== null && emit('update:modelValue', currentDragValue.value)

  currentDragValue.value = null
  isMoving.value = false
}

const onLineClick = (e: MouseEvent) => {
  if (props.disabled) return

  hasInteracted.value = true

  updateSizes()

  const rect = line.value?.getBoundingClientRect()
  if (!rect) return

  const x = e.clientX - rect.left

  const steps = Math.round((x - handleWidth.value / 2) / stepWidth.value)

  const value = clamp(minN.value + steps * stepN.value)

  emit('update:modelValue', value)
}

onMounted(() => {
  updateSizes()
  window.addEventListener('resize', updateSizes)
  document.addEventListener('mousemove', onMove)
  document.addEventListener('touchmove', onMove, { passive: false })
  document.addEventListener('mouseup', onEnd)
  document.addEventListener('touchend', onEnd)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateSizes)
  document.removeEventListener('mousemove', onMove)
  document.removeEventListener('touchmove', onMove)
  document.removeEventListener('mouseup', onEnd)
  document.removeEventListener('touchend', onEnd)
})
</script>

<style lang="sass" scoped>
.rangeSelectComponent
  user-select: none
  margin: 0 5*$u
  height: 6*$u
  position: relative
  top: 3*$u
  display: flex

  .line
    width: 100%
    height: 3px
    background-color: $colorGray2
    position: relative

  .range
    position: absolute
    height: 3px
    background-color: $colorPrimary
    left: 0
    top: 0

  .handle
    width: 10*$u
    height: 6*$u
    background-color: $colorPrimary
    border-radius: $brM
    display: flex
    position: absolute
    top: -3*$u
    transform: translateX(-50%)
    cursor: grab
    z-index: 2

    &._disabled
      opacity: 0.9
      cursor: not-allowed

  .value
    margin: auto
    color: $colorWhite
    @include font('t14')
</style>
